import { importProvidersFrom, inject, Injectable, provideAppInitializer } from '@angular/core';

import * as Sentry from '@sentry/capacitor';

import { AccountBase, RtcErrorHandlerModule } from '@core/common-legacy';
import { injectDevFlagsPromise } from '@core/tellent-dev-flags-logic';
import {
  injectTellentAppKind,
  ORG_GUID,
  TELLENT_INITIAL_CURRENT_USER,
  TellentClientConfig,
} from '@core/tellent-types';

import { AppRevisionProvider, AppRevisionToken } from './app.revision';
import { environment } from '../environments/environment';

@Injectable()
export class Account extends AccountBase {}

export const resolveErrorHandlerProviders = (
  { env: runtimeEnv }: TellentClientConfig,
  appGuid?: string,
) => {
  const ERR_TRACKING_ENABLED =
    environment.angularProdMode && runtimeEnv !== 'local' && runtimeEnv !== 'local-staging';

  if (!ERR_TRACKING_ENABLED) return [AppRevisionProvider]; // for logging purposes only

  return [
    { provide: Account, useClass: Account },
    AppRevisionProvider,
    importProvidersFrom(
      RtcErrorHandlerModule.forRoot(
        {
          enable: ERR_TRACKING_ENABLED,
          dsn: 'https://2e1336227d62f5a420f1b5960ba6acb5@o4511234536570880.ingest.de.sentry.io/4511234584739920',
          revisionToken: AppRevisionToken,
          environmentName: runtimeEnv,
        },
        Account,
      ),
    ),
    provideAppInitializer(async () => {
      try {
        const appKind = injectTellentAppKind();
        const orgGuid = inject(ORG_GUID);
        const user = inject(TELLENT_INITIAL_CURRENT_USER);
        const member = user.members.find(member => member.orgGuid === orgGuid)!;
        const devFlags = await injectDevFlagsPromise();

        Sentry.setUser({
          id: member?.guid,
          email: member?.email,
          username: `${member?.firstName} ${member?.lastName}`,
        });

        Sentry.setTags({
          OrgGuid: orgGuid,
          AppKind: appKind,
          AppGuid: appGuid,
          ...Object.fromEntries(
            Object.entries(devFlags || {}).map(([k, v]) => [`DevFlag.${k}`, v]),
          ),
        });
      } catch {
        return Promise.resolve();
      }

      return Promise.resolve();
    }),
  ];
};
